package uk.co.nickthecoder.glok.backend.webgl

import org.khronos.webgl.TexImageSource
import org.khronos.webgl.Uint8Array
import org.khronos.webgl.WebGLRenderingContext
import org.khronos.webgl.WebGLRenderingContext.Companion.CLAMP_TO_EDGE
import org.khronos.webgl.WebGLRenderingContext.Companion.LINEAR
import org.khronos.webgl.WebGLRenderingContext.Companion.REPEAT
import org.khronos.webgl.WebGLRenderingContext.Companion.RGBA
import org.khronos.webgl.WebGLRenderingContext.Companion.TEXTURE_2D
import org.khronos.webgl.WebGLRenderingContext.Companion.TEXTURE_MIN_FILTER
import org.khronos.webgl.WebGLRenderingContext.Companion.TEXTURE_WRAP_S
import org.khronos.webgl.WebGLRenderingContext.Companion.TEXTURE_WRAP_T
import org.khronos.webgl.WebGLRenderingContext.Companion.UNSIGNED_BYTE
import org.khronos.webgl.WebGLTexture
import uk.co.nickthecoder.glok.application.Application
import uk.co.nickthecoder.glok.backend.AbstractTexture
import uk.co.nickthecoder.glok.backend.Texture
import uk.co.nickthecoder.glok.scene.Color
import uk.co.nickthecoder.glok.util.log
import org.w3c.dom.Image as DOMImage

interface WebGLTextureWrapper : Texture {
    val texture: WebGLTexture?
}

internal class LoadedWebGLTexture(

    override val name: String,
    override val width: Int,
    override val height: Int,
    image: TexImageSource?,
    color: Color = Color.TRANSPARENT

) : AbstractTexture(), WebGLTextureWrapper {

    constructor(name: String, image: DOMImage) : this(name, image.width, image.height, image)

    constructor(name: String, width: Int, height: Int, color: Color) : this(name, width, height, null, color)

    // Used to create a dummy (empty) texture
    constructor(name: String) : this(name, 1, 1, null)

    override val texture: WebGLTexture? = WebGLBackend.gl.createTexture()

    init {
        if (texture == null) {
            log.error("Failed to create WebGLTexture")
        }

        with(WebGLBackend.gl) {
            bindTexture(TEXTURE_2D, texture)
            if (image == null) {
                val red = (color.red * 255).toInt().toByte()
                val green = (color.green * 255).toInt().toByte()
                val blue = (color.blue * 255).toInt().toByte()
                val alpha = (color.alpha * 255).toInt().toByte()
                // Isn't there a better way to do this?
                val data = Uint8Array(Array<Byte>(width * height * 4) { i ->
                    val channel = i % 4
                    when (channel) {
                        0 -> red
                        1 -> green
                        2 -> blue
                        else -> alpha
                    }
                })
                texImage2D(TEXTURE_2D, level = 0, RGBA, width, height, border = 0, RGBA, UNSIGNED_BYTE, data)
            } else {
                texImage2D(TEXTURE_2D, level = 0, RGBA, RGBA, UNSIGNED_BYTE, image)
            }

            texParameteri(TEXTURE_2D, TEXTURE_WRAP_S, CLAMP_TO_EDGE)
            texParameteri(TEXTURE_2D, TEXTURE_WRAP_T, CLAMP_TO_EDGE)
            texParameteri(TEXTURE_2D, TEXTURE_MIN_FILTER, LINEAR)
            isLoaded = true
        }
    }

    override fun bind() {
        WebGLBackend.gl.bindTexture(TEXTURE_2D, texture)
    }

    override fun delete() {
        WebGLBackend.gl.deleteTexture(texture)
    }

    override fun setRepeating(x: Boolean, y: Boolean) {
        with(WebGLBackend.gl) {
            bind()
            texParameteri(TEXTURE_2D, TEXTURE_WRAP_S, if (x) REPEAT else CLAMP_TO_EDGE)
            texParameteri(TEXTURE_2D, TEXTURE_WRAP_T, if (y) REPEAT else CLAMP_TO_EDGE)
        }
    }

    override fun toString() = "Texture '$name' $width x $height"

}

/**
 * When we load textures from the web server, the results aren't available until the data transfer is complete.
 * Therefore, we cannot return a [LoadedWebGLTexture], instead we use [DelayedTexture], which appears as a
 * blank image of size 1x1 until the image has been loaded.
 *
 * When the image is loaded, the scene will automatically be laid out and redrawn.
 *
 * BEWARE, there are some situations where this automatic update isn't sufficient.
 * For example, if you use the [width] or [height] before it has loaded, then the data will be incorrect.
 */
internal class DelayedTexture(
    override val name: String,
    url: String
) : AbstractTexture(), WebGLTextureWrapper {

    private var actual: LoadedWebGLTexture? = null

    private var repeatX = false
    private var repeatY = false

    init {
        val image = DOMImage()
        image.onload = { e ->
            actual = LoadedWebGLTexture(name, image)
            actual?.setRepeating(repeatX, repeatY)
            //log.info("Loaded image '$name' $actual")

            isLoaded = true
            // Now that the texture has been loaded, ensure that the scene is redrawn,
            // so that any uses of this texture are shown correctly.
            // ImageView takes care of itself when the image is a Texture, but does NOT when the image
            // is an icon in a IconSheet. Thankfully we only need to redraw, because the icon sheet
            // knows the sizes of each icon.
            Application.stages.firstOrNull()?.scene?.root?.requestRedraw()

            // Arbitrary return value (not used).
            actual
        }
        image.src = url
    }

    override val width: Int
        get() = actual?.width ?: 1

    override val height: Int
        get() = actual?.height ?: 1

    override val texture: WebGLTexture?
        get() = actual?.texture

    override fun bind() {
        WebGLBackend.gl.bindTexture(TEXTURE_2D, actual?.texture)
    }

    override fun delete() {
        actual?.delete()
    }

    override fun setRepeating(x: Boolean, y: Boolean) {
        repeatX = x
        repeatY = y
        actual?.setRepeating(x, y)
    }

    override fun toString(): String = actual?.toString() ?: "Texture '$name' (not yet loaded)"

}
